import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import isDesktop from 'store/app/selector/isDesktop';
import OpText from 'components/Common/op-ui-wrappers/OpText/OpText';
import cn from 'classnames';
import OpIcon from 'components/Common/op-ui-wrappers/OpIcon/OpIcon';
import getConfigurationByKey from 'store/app/selector/getConfigurationByKey';
import gtmSendEvent from 'utils/gtm/gtmSendEvent';
import styles from './PlusMembershipInfo.module.scss';

const PlusMembershipInfoItem = ({
  title,
  label,
  cta,
  description,
  endDate,
  className,
  hasContactCustomerSupport,
}: {
  title?: string;
  label?: string;
  cta?: JSX.Element;
  description?: string;
  endDate?: string;
  className?: 'danger' | 'warning';
  hasContactCustomerSupport?: boolean;
}) => {
  const isDesktopDevice = useSelector(isDesktop);
  const { t } = useTranslation();
  const customerCareUrl = useSelector(getConfigurationByKey('customerCareUrl'));

  const onContactSupportEvent = () => {
    gtmSendEvent({
      event: 'your_dashboard',
      click: 'plus section contact customer support',
    });
  };

  return (
    <div className={styles.item}>
      {title && (
        <OpText block variant="title-small-bold" className={styles.membershipInfoItemTitle}>
          {title}
        </OpText>
      )}
      {label && (
        <OpText
          className={styles.membershipInfoContent}
          block
          variant={isDesktopDevice ? 'title-large-regular' : 'title-medium-regular'}
        >
          <Trans t={t} i18nKey={label} />
        </OpText>
      )}
      {description && (
        <div className={cn(styles.membershipDescription, { [styles.error]: className === 'danger' })}>
          {className && <OpIcon className={styles[`icon-${className}`]} icon={className} />}
          <OpText
            className={cn(styles.membershipInfoContent, styles[className || ''])}
            block
            variant="body-large-regular"
          >
            <Trans t={t} i18nKey={description} values={{ date: endDate }} components={{ u: <u />, b: <b /> }} />
          </OpText>
        </div>
      )}
      {cta}
      {hasContactCustomerSupport && (
        <a href={customerCareUrl} className={styles.contactSupport} onClick={onContactSupportEvent}>
          <OpText variant="body-special-medium-400">
            {isDesktopDevice ? t('txt_plus_customer_support_end_membership') : t('txt_end_membership')}
          </OpText>
        </a>
      )}
    </div>
  );
};

const PaymentInfoItem = ({ subscriptionData, t }: { subscriptionData: any; t: any }) => (
  <>
    <PlusMembershipInfoItem
      title={t('txt_plus_renewal_date')}
      label={subscriptionData?.subscriptionInfo.renewalDateFormatted}
    />
    <PlusMembershipInfoItem
      title={t('txt_last_payment')}
      label={subscriptionData?.subscriptionInfo.startDateFormatted}
    />
  </>
);

const UpdateMembershipItem = ({
  status,
  cta,
  hasContactCustomerSupport,
}: {
  status: any;
  cta: any;
  hasContactCustomerSupport?: boolean;
}) =>
  status && (
    <PlusMembershipInfoItem
      title={status.title}
      description={status.description}
      className={status.className}
      label={status.label}
      endDate={status.endDate}
      cta={cta}
      hasContactCustomerSupport={hasContactCustomerSupport}
    />
  );

export { PaymentInfoItem, UpdateMembershipItem, PlusMembershipInfoItem };
